//codex-- Keep the toolbar redesign scoped to pages in this folder.
import PageToolbar from './PageToolbar';
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import SubmitButton from '../components/SubmitButton';
import HeadingCp from '../components/HeadingCp';
import LoadingSpinner from '../components/LoadingSpinner';
import { NavLink } from 'react-router-dom';

const MaintenanceConfigPage = () => {
  const apiUrl = process.env.REACT_APP_API_URL;
  const [formData, setFormData] = useState({
    maintenance_config_id: '',
    min_per_output: '',
    min_per_input: '',
    overtime_increase: '',
    night_differential: '',
    degree_difficulty: '',
  });
  const [configs, setConfigs] = useState(null);
  const [loading, setLoading] = useState(false);
  const [isEditing, setIsEditing] = useState(false);

  useEffect(() => {
    fetchConfigs();
  }, []);

  const fetchConfigs = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${apiUrl}/contracts_crud.php?getEstimateConfigs=true`);
      if (response.data[0] === 'success') {
        setConfigs(response.data[1]);
      } else {
        toast.error('No data.', { position: 'top-center' });
      }
    } catch (error) {
      toast.error('Error loading data.', { position: 'top-center' });
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleEdit = (config) => {
    setFormData(config);
    setIsEditing(true);
  };

  const handleDelete = async (id) => {
    try {
      const response = await axios.post(`${apiUrl}/contracts_crud.php`, {
        status: 'deleteEstimateConfig',
        data: {maintenance_config_id:id }
      });
      if (response.data.success) {
        toast.success('Deleted successfully', { position: 'top-center' });
        fetchConfigs();
      } else {
        toast.error('Failed to delete.', { position: 'top-center' });
      }
    } catch (error) {
      toast.error('Error deleting config.', { position: 'top-center' });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const response = await axios.post(`${apiUrl}/contracts_crud.php`, {
        status: 'submitEstimateConfig',
        data: formData,
      });
      if (response.data.success) {
        toast.success('Saved successfully!', { position: 'top-center' });
        setFormData({
          maintenance_config_id: '',
          min_per_output: '',
          min_per_input: '',
          overtime_increase: '',
          night_differential: '',
          degree_difficulty: '',
        });
        setIsEditing(false);
        fetchConfigs();
      } else {
        toast.error(response.data.error || 'Failed to save.', { position: 'top-center' });
      }
    } catch (error) {
      toast.error('Error saving config.', { position: 'top-center' });
    }
  };

  return (
    <div className="mx-auto max-w-3xl px-2 py-10">
      <HeadingCp label="Maintenance Estimate Configuration" />

      <>{/*codex-- Match the updated toolbar: grouped actions, labeled controls, and lighter original colors. */}
        <PageToolbar title="Estimate configuration tools" actions={<>
          <NavLink to="/maintenance_estimate">
            <button type="button" className="px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-orange-400 hover:bg-orange-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500">New Estimate</button>
          </NavLink>
          <NavLink to="/maintenance_list">
            <button type="button" className="px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-400 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500">Estimate List</button>
          </NavLink>
        </>} />
      </>

      <form onSubmit={handleSubmit} className="space-y-6 bg-white p-6 border rounded shadow">
        <div className="flex flex-wrap gap-4">
          <input
            type="number"
            name="min_per_output"
            value={formData.min_per_output}
            onChange={handleInputChange}
            placeholder="Min Per Output"
            required
            className="form-input w-48"
          />
          <input
            type="number"
            name="min_per_input"
            value={formData.min_per_input}
            onChange={handleInputChange}
            placeholder="Min Per Input"
            required
            className="form-input w-48"
          />
          <input
            type="number"
            step="0.01"
            name="overtime_increase"
            value={formData.overtime_increase}
            onChange={handleInputChange}
            placeholder="Overtime Increase"
            className="form-input w-48"
          />
          <input
            type="number"
            step="0.01"
            name="night_differential"
            value={formData.night_differential}
            onChange={handleInputChange}
            placeholder="Night Differential"
            className="form-input w-48"
          />
          <input
            type="number"
            step="0.01"
            name="degree_difficulty"
            value={formData.degree_difficulty}
            onChange={handleInputChange}
            placeholder="Degree of Difficulty"
            className="form-input w-48"
          />
        </div>
        <SubmitButton>{isEditing ? 'Update' : 'Save'}</SubmitButton>
      </form>
      <hr className="my-6" />
      {loading ? (
        <LoadingSpinner />
      ) : (
        <div className="overflow-x-auto border rounded shadow mt-6">
          <table className="min-w-full text-sm">
            <thead>
              <tr className="bg-gray-100">
                <th className="p-2 text-left">ID</th>
                <th className="p-2 text-left">Min Output</th>
                <th className="p-2 text-left">Min Input</th>
                <th className="p-2 text-left">Actions</th>
              </tr>
            </thead>
            <tbody>
              {configs && (
                <tr className="border-t">
                  <td className="p-2">{configs.id}</td>
                  <td className="p-2">{configs.min_per_output}</td>
                  <td className="p-2">{configs.min_per_input}</td>
                  <td className="p-2 space-x-2">
                    <button onClick={() => handleEdit(configs)} className="text-blue-600 hover:underline">Edit</button>
                    <button onClick={() => handleDelete(configs.id)} className="text-red-600 hover:underline">Delete</button>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default MaintenanceConfigPage;
